import clsx from 'clsx';
import { useState, useRef, useEffect } from 'react';
import { useBetslipStore } from '@/store/betslip';
import { BackspaceIcon, ViewMyBetIcon, ArrowRightIcon } from '@/components/icons/svg-icons';
import { bettingInputMaxLength } from '@/hooks/query';
import { addCommasWithFix } from '@/utils/odds-utils';
import { useQueryClient } from '@tanstack/react-query';
import { accountService } from '@/services/account.service';
import { useGetAccountSetting } from '@/hooks/query/use-account-settings-data';
import { useLocale, useTranslations } from 'next-intl';
import { setKafkaNormal } from '@/hooks/query/use-kafka-log';
import { useBetslipContext } from '@/features/betslip/contexts/betslip-context';
import { usePathname, useRouter } from '@/navigation';
import { betslipStyle } from '@/features/betslip/betslip.style';
import { useAppearanceStore } from '@/store/appearance';
import { ViewEnum } from '@/types';
import { routerService } from '@/services/router.service';
import { KafkaNote } from '@nf/utils-common/constants';
import { quickBetStyle } from './quick-bet.style';
import { useAsideBetslip } from '@/features/betslip/hook/use-aside-betslip';

export const QuickBet = () => {
	const t = useTranslations('General');
	const router = useRouter();
	const locale = useLocale();
	const pathName = usePathname();
	const { userSetting } = useGetAccountSetting();
	const { quickBetStake, currencyName } = useBetslipContext();
	const { activeAsideBetSlip } = useAsideBetslip();
	const queryClient = useQueryClient();

	const [
		focusOddsKey,
		selectAllOddsKey,
		isQuickbetExpand,
		setFocusOddsKey,
		setSelectAllOddsKey
	] = useBetslipStore(state => [
		state.focusOddsKey,
		state.selectAllOddsKey,
		state.isQuickbetExpand,
		state.setFocusOddsKey,
		state.setSelectAllOddsKey
	]);

	const input = useRef<HTMLInputElement>(null);
	const [stake, setStake] = useState(quickBetStake); // -1: 未設定過, 0: 不可存
	const key = 'quickBet';
	const isSelectAll = key === selectAllOddsKey;
	const isDisabled = stake === quickBetStake || stake <= 0;
	const view = useAppearanceStore(state => state.view);
	const getDisplayStake = () => (stake === -1 ? '0' : stake ? addCommasWithFix(stake) : '');

	const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		const code = e.code;
		const isAlphaPressed = code.startsWith('Key');

		if (isAlphaPressed && !e.ctrlKey) {
			e.preventDefault();
			return;
		}

		const stakeLength = (stake ?? 0).toString().length;

		if (stakeLength >= bettingInputMaxLength) {
			setStake(0);
		}
	}

	const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const input = e.target.value;
		const inputStake = parseInt(input.toString().replace(/[^0-9]/g, '')) || 0;
		setStake(inputStake);
		setSelectAllOddsKey('');
	}

	const onClick = () => {
		if (!isDisabled) {
			setFocusOddsKey('');
			if (userSetting) {
				accountService.setQuickBetStake(queryClient, stake, userSetting);
			}
		}
	}

	const onFocus = () => {
		setFocusOddsKey(key);

		if (stake) {
			setSelectAllOddsKey(key);
		} else {
			setSelectAllOddsKey('');
		}

		input.current?.select();
	}

	const onNumberPress = (value: string) => {
		const stakeLength = (stake ?? 0).toString().length;

		if (stakeLength >= bettingInputMaxLength || isSelectAll) {
			setSelectAllOddsKey('');
			setStake(Number(value));
			return;
		}

		const newStake = stake + value;

		setSelectAllOddsKey('');
		setStake(Number(newStake));
	};

	const onBackspacePress = () => {
		let stakeStr = (stake ?? 0).toString();

		if (isSelectAll) {
			stakeStr = '0';
		} else {
			stakeStr = stakeStr.substring(0, stakeStr.length - 1);
		}

		setSelectAllOddsKey('');
		setStake(Number(stakeStr));
	}

	const onDonePress = () => {
		setFocusOddsKey('');
		setSelectAllOddsKey('');
	}

	const openHowToUse = () => {
		setKafka();
		routerService.push(router.push, locale, '/how-to-use?tabName=quick-bet', pathName);
	}

	const setKafka = () => {
		const kafkaData = {
			note: KafkaNote.GalaxyV2,
			type: 8,
			action: 4
		}
		setKafkaNormal(kafkaData);
	}

	useEffect(() => {
		if (stake <= 0) {
			setFocusOddsKey(key);
			setSelectAllOddsKey(key);
		}
	}, []);

	useEffect(() => {
		setStake(quickBetStake);
	}, [quickBetStake]);

	return (
		<div className={clsx('bet-slip__container', { [betslipStyle.betslipContainer]: activeAsideBetSlip })}>
			{
				isQuickbetExpand ?
					<div className={quickBetStyle.quickBetWrapper}>
						<div
							className={clsx('stake-input', betslipStyle.betInput)}
							data-selected={isSelectAll}
							onClick={onFocus}
						>
							<div className="stake-input__stake">
								<div className="stake-input__textfield">
									<div className="stake-input__currency">{currencyName}</div>
									<div className="stake-input__input">{getDisplayStake()}</div>
									<input
										ref={input}
										className="input"
										type="input"
										value={getDisplayStake()}
										onKeyDown={onKeyDown}
										onChange={onChange}
										onFocus={onFocus}
									/>
								</div>
							</div>
						</div>
						<button
							className={clsx('btn btn--primary', quickBetStyle.betButton)}
							data-disabled={isDisabled}
							onClick={onClick}
						>
							<span className="btn__text">{t('lbl_Save')}</span>
						</button>
					</div> : null
			}
			{
				isQuickbetExpand ?
					<div className="bet-slip__keypad" data-show={key === focusOddsKey}>
						<div className="keypad keypad--default-mini">
							<div className="keypad__numbers">
								{
									['1', '2', '3', '4', '5', '6', '7', '8', '9', '0', '00'].map(number => {
										return (
											<div
												key={number}
												className="keypad-btn"
												onClick={() => onNumberPress(number)}
											>
												{number}
											</div>
										)
									})
								}
								<div className="keypad__backspace" onClick={onBackspacePress}>
									<div className="keypad-btn">
										<BackspaceIcon />
									</div>
								</div>
							</div>
							<div className="keypad__submit" onClick={onDonePress}>
								<div className="keypad-btn">{t('lbl_Done')}</div>
							</div>
						</div>
					</div> : null
			}
			<div className="bet-slip__info">
				<ol>
					<li>
						<ViewMyBetIcon />{view === ViewEnum.galaxy ? t('lbl_QuickBetDescrip') : t('lbl_MarsQuickBetDescrip')}
					</li>
					{view === ViewEnum.galaxy ? 
						<li>
							{t('lbl_QuickBetEnabled')}
							<button className="bet-slip__info-btn" onClick={openHowToUse}>
								{t('lbl_howToUse')}
								<ArrowRightIcon />
							</button>
						</li> : null}
				</ol>
			</div>
		</div>
	);
}